import {l} from "types/typescriptdefinisjoner_lederplan";
import {BasisRessurs} from "felles/infrastruktur/BasisRessurs";
import {Strenger} from "../Strenger";
import {RessursFeilhåndtering} from "felles/tjenester/RessursFeilhåndtering";
import {gettextCatalog} from "angular-gettext";
import _ from "lodash";
import moment from "moment";
import {regNavn} from "../tredjepartsTjenestenavn";
import {Språk} from "../i18n/Spraak";
import {iDagKl00, somKl0000} from "../../stoettefunk/datoer";
import {Konfigurasjon} from "../../infrastruktur/Konfigurasjon";
import {Debugger} from "debug";
import {uriEnkodet} from "../../stoettefunk/uri";
import {Filer} from "../Filer";
import {IPromise} from "angular";

const debug: Debugger = require('debug')('AvtaleRessurs');

export class AvtaleRessurs extends BasisRessurs<l.AvtaledetaljerModell> {

    static regNavn = "avtaleRessurs";

    static forInjisering() {
        return [
            "$resource",
            Konfigurasjon.regNavn,
            Strenger.regNavn,
            RessursFeilhåndtering.regNavn,
            regNavn.gettext.gettextCatalog,
            AvtaleRessurs
        ]
    }

    constructor($resource: ng.resource.IResourceService,
                private konfigurasjon: Konfigurasjon,
                private strenger: Strenger,
                ressursFeilhåndtering: RessursFeilhåndtering,
                gettextCatalog: gettextCatalog) {
        super("avtale",
            $resource,
            ressursFeilhåndtering,
            gettextCatalog,
            {
                hentEnkel: {method: 'GET', url: "/api/avtale/:id/enkel"},
                finnAvtalerBrukerErKontaktpersonFor: {method: 'GET', url: "/api/avtale/finnAvtalerBrukerErKontaktpersonFor", isArray: true},
                finnAlle: {method: 'GET', url: "/api/avtale/finnAlle", isArray: true},
                finnAvtalerBrukerHarAktivtMedlemskapI: {method: 'GET', url: "/api/avtale/finnAvtalerBrukerHarAktivtMedlemskapI", isArray: true},
                finnBrregAvtaleinformasjon: {method: 'GET', url: "/api/avtale/finnBrregAvtaleinformasjon", isArray: true},
                hentVilkår: {method: 'GET', url: "/api/avtale/hentVilkaar"},
                kanAvtaleTermineres: {method: 'GET', url: "/api/avtale/:id/kanAvtaleTermineres", params: {id: '@id'}},
                oppdaterFaktureringsinformasjon: {method: 'POST', url: "/api/avtale/:avtaleId/oppdaterFaktureringsinformasjon", params: {avtaleId: '@avtaleId'}},
                oppdaterProeveperiodeUtloepsdato: {method: 'POST', url: "/api/avtale/:id/oppdaterProeveperiodeUtloepsdato", params: {id: '@id'}},
                // terminerAvtale: {method: 'POST', url: "/api/avtale/:id/terminerAvtale", params: {id: '@id'}}, // kun i ng2
                adminTerminerAvtale: {method: 'POST', url: "/api/avtale/:id/adminTerminerAvtale", params: {id: '@id'}},
                reaktiverAvtale: {method: 'POST', url: "/api/avtale/:id/reaktiverAvtale", params: {id: '@id'}}
            });
    }

    //offentlige metoder

    hentEnkel(id:string): l.AvtaleModell {
        debug("hentEnkel");
        return this.ressurs["hentEnkel"](
            {id:id},
            _.noop,
            this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke laste avtale"))
        );
    }

    finnAlle(): l.AvtaleModell[] {
        debug("finnAlle");
        return this.ressurs["finnAlle"](
            {},
            _.noop,
            this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke laste alle avtaler"))
        );
    }

    finnAvtalerBrukerErKontaktpersonFor(): l.AvtaleModell[] {
        debug("finnAvtalerBrukerErKontaktpersonFor");
        return this.ressurs["finnAvtalerBrukerErKontaktpersonFor"](
            {},
            _.noop,
            this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke laste avtaler bruker er kontaktperson for"))
        );
    }

    finnAvtalerBrukerHarAktivtMedlemskapI(): l.AvtaleModell[] {
        debug("finnAvtalerBrukerHarAktivtMedlemskapI");
        return this.ressurs["finnAvtalerBrukerHarAktivtMedlemskapI"](
            {},
            _.noop,
            this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke laste tilgjengelige avtaler"))
        );
    }

    finnBrregAvtaleinformasjon(firmanavn: string): l.BrregEnhet[] {
        debug("finnBrregAvtaleinformasjon", firmanavn);
        return this.ressurs["finnBrregAvtaleinformasjon"](
            {firmanavn: firmanavn},
            _.noop,
            this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke laste organisasjonsinformasjon fra Brønnøysundregisteret"))
        );
    }

    hentVilkår(språk: Språk): l.BetingelserModell {
        debug("hentVilkår");
        return this.ressurs["hentVilkår"](
            {spraak: språk.servertag},
            _.noop,
            this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke laste betingelser"))
        );
    }

    oppdaterFaktureringsinformasjon(spesifikasjon: l.RedigerFakturainformasjonSpesifikasjon) {
        debug("oppdaterFaktureringsinformasjon", spesifikasjon);
        return this.ressurs["oppdaterFaktureringsinformasjon"](
            spesifikasjon,
            _.noop,
            this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke oppdatere faktureringsinformasjon"))
        );
    }

    oppdaterProeveperiodeUtloepsdato(spesifikasjon: l.PrøveperiodeUtløpsdatoSpesifikasjon): l.AvtaleModell {
        debug("oppdaterProeveperiodeUtloepsdato", spesifikasjon);
        return this.ressurs["oppdaterProeveperiodeUtloepsdato"](
            spesifikasjon,
            _.noop,
            this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke oppdatere prøveperiode"))
        );
    }

    kanAvtaleTermineres(id:string): IPromise<l.KanAvtaleTermineresInfoModell2> {
        debug("kanAvtaleTermineres", id);
        return this.ressurs["kanAvtaleTermineres"](
          {id:id},
          _.noop,
          this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke avgjøre om avtale kan avsluttes"))
        ).$promise;
    }

    adminTerminerAvtale(id:string): IPromise<l.TerminerAvtaleResponsModell> {
        debug("adminTerminerAvtale", id);
        return this.ressurs["adminTerminerAvtale"](
          {id:id},
          _.noop,
          this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke avslutte avtale"))
        ).$promise;
    }

    reaktiverAvtale(id:string): l.AvtaleModell {
        debug("reaktiverAvtale", id);
        return this.ressurs["reaktiverAvtale"](
            {id:id},
            _.noop,
            this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke reaktivere avtale"))
        );
    }

    public avtaledumpUrl(avtale: l.AvtaleModell) {
        return `${this.konfigurasjon.serviceRotUrl}/api/avtale/${avtale.id}/datadump/${uriEnkodet(Filer.somSikkertFilnavn(avtale.navn))}.zip`
    }


    static landkoder(): { [landkode: string]: string } {
//kopiert fra https://help.surveymonkey.com/articles/en_US/kb/Do-you-have-a-list-of-countries
        const land: { [landkode: string]: string } = {
            AD: 'Andorra',
            AE: 'United Arab Emirates',
            AF: 'Afghanistan',
            AG: 'Antigua and Barbuda',
            AI: 'Anguilla',
            AL: 'Albania',
            AM: 'Armenia',
            AO: 'Angola',
            AQ: 'Antarctica',
            AR: 'Argentina',
            AS: 'American Samoa',
            AT: 'Austria',
            AU: 'Australia',
            AW: 'Aruba',
            AZ: 'Azerbaijan',
            BA: 'Bosnia and Herzegovina',
            BB: 'Barbados',
            BD: 'Bangladesh',
            BE: 'Belgium',
            BF: 'Burkina Faso',
            BG: 'Bulgaria',
            BH: 'Bahrain',
            BI: 'Burundi',
            BJ: 'Benin',
            BL: 'Saint Barthelemy',
            BM: 'Bermuda',
            BN: 'Brunei',
            BO: 'Bolivia',
            BR: 'Brazil',
            BS: 'Bahamas, The',
            BT: 'Bhutan',
            BV: 'Bouvet Island',
            BW: 'Botswana',
            BY: 'Belarus',
            BZ: 'Belize',
            CA: 'Canada',
            CC: 'Cocos (Keeling) Islands',
            CD: 'Congo, Democratic Republic of the',
            CF: 'Central African Republic',
            CG: 'Congo, Republic of the',
            CH: 'Switzerland',
            CI: "Cote d'Ivoire",
            CK: 'Cook Islands',
            CL: 'Chile',
            CM: 'Cameroon',
            CN: 'China',
            CO: 'Colombia',
            CR: 'Costa Rica',
            CU: 'Cuba',
            CV: 'Cape Verde',
            CW: 'Curacao',
            CX: 'Christmas Island',
            CY: 'Cyprus',
            CZ: 'Czech Republic',
            DE: 'Germany',
            DJ: 'Djibouti',
            DK: 'Denmark',
            DM: 'Dominica',
            DO: 'Dominican Republic',
            DZ: 'Algeria',
            EC: 'Ecuador',
            EE: 'Estonia',
            EG: 'Egypt',
            EH: 'Western Sahara',
            ER: 'Eritrea',
            ES: 'Spain',
            ET: 'Ethiopia',
            FI: 'Finland',
            FJ: 'Fiji',
            FK: 'Falkland Islands (Islas Malvinas)',
            FM: 'Micronesia, Federated States of',
            FO: 'Faroe Islands',
            FR: 'France',
            FX: 'France, Metropolitan',
            GA: 'Gabon',
            GB: 'United Kingdom',
            GD: 'Grenada',
            GE: 'Georgia',
            GF: 'French Guiana',
            GG: 'Guernsey',
            GH: 'Ghana',
            GI: 'Gibraltar',
            GL: 'Greenland',
            GM: 'Gambia, The',
            GN: 'Guinea',
            GP: 'Guadeloupe',
            GQ: 'Equatorial Guinea',
            GR: 'Greece',
            GS: 'South Georgia and the Islands',
            GT: 'Guatemala',
            GU: 'Guam',
            GW: 'Guinea-Bissau',
            GY: 'Guyana',
            HK: 'Hong Kong',
            HM: 'Heard Island and McDonald Islands',
            HN: 'Honduras',
            HR: 'Croatia',
            HT: 'Haiti',
            HU: 'Hungary',
            ID: 'Indonesia',
            IE: 'Ireland',
            IL: 'Israel',
            IM: 'Isle of Man',
            IN: 'India',
            IO: 'British Indian Ocean Territory',
            IQ: 'Iraq',
            IR: 'Iran',
            IS: 'Iceland',
            IT: 'Italy',
            JE: 'Jersey',
            JM: 'Jamaica',
            JO: 'Jordan',
            JP: 'Japan',
            KE: 'Kenya',
            KG: 'Kyrgyzstan',
            KH: 'Cambodia',
            KI: 'Kiribati',
            KM: 'Comoros',
            KN: 'Saint Kitts and Nevis',
            KP: 'Korea, North',
            KR: 'Korea, South',
            KW: 'Kuwait',
            KY: 'Cayman Islands',
            KZ: 'Kazakhstan',
            LA: 'Laos',
            LB: 'Lebanon',
            LC: 'Saint Lucia',
            LI: 'Liechtenstein',
            LK: 'Sri Lanka',
            LR: 'Liberia',
            LS: 'Lesotho',
            LT: 'Lithuania',
            LU: 'Luxembourg',
            LV: 'Latvia',
            LY: 'Libya',
            MA: 'Morocco',
            MC: 'Monaco',
            MD: 'Moldova',
            ME: 'Montenegro',
            MF: 'Saint Martin',
            MG: 'Madagascar',
            MH: 'Marshall Islands',
            MK: 'Macedonia',
            ML: 'Mali',
            MM: 'Burma',
            MN: 'Mongolia',
            MO: 'Macau',
            MP: 'Northern Mariana Islands',
            MQ: 'Martinique',
            MR: 'Mauritania',
            MS: 'Montserrat',
            MT: 'Malta',
            MU: 'Mauritius',
            MV: 'Maldives',
            MW: 'Malawi',
            MX: 'Mexico',
            MY: 'Malaysia',
            MZ: 'Mozambique',
            NA: 'Namibia',
            NC: 'New Caledonia',
            NE: 'Niger',
            NF: 'Norfolk Island',
            NG: 'Nigeria',
            NI: 'Nicaragua',
            NL: 'Netherlands',
            NO: 'Norway',
            NP: 'Nepal',
            NR: 'Nauru',
            NU: 'Niue',
            NZ: 'New Zealand',
            OM: 'Oman',
            PA: 'Panama',
            PE: 'Peru',
            PF: 'French Polynesia',
            PG: 'Papua New Guinea',
            PH: 'Philippines',
            PK: 'Pakistan',
            PL: 'Poland',
            PM: 'Saint Pierre and Miquelon',
            PN: 'Pitcairn Islands',
            PR: 'Puerto Rico',
            PS: 'Palestinian Territory',
            PT: 'Portugal',
            PW: 'Palau',
            PY: 'Paraguay',
            QA: 'Qatar',
            RE: 'Reunion',
            RO: 'Romania',
            RS: 'Serbia',
            RU: 'Russia',
            RW: 'Rwanda',
            SA: 'Saudi Arabia',
            SB: 'Solomon Islands',
            SC: 'Seychelles',
            SD: 'Sudan',
            SE: 'Sweden',
            SG: 'Singapore',
            SH: 'Saint Helena, Ascension, and Tristan da Cunha',
            SI: 'Slovenia',
            SJ: 'Svalbard',
            SK: 'Slovakia',
            SL: 'Sierra Leone',
            SM: 'San Marino',
            SN: 'Senegal',
            SO: 'Somalia',
            SR: 'Suriname',
            SS: 'South Sudan',
            ST: 'Sao Tome and Principe',
            SV: 'El Salvador',
            SX: 'Sint Maarten',
            SY: 'Syria',
            SZ: 'Swaziland',
            TC: 'Turks and Caicos Islands',
            TD: 'Chad',
            TF: 'French Southern and Antarctic Lands',
            TG: 'Togo',
            TH: 'Thailand',
            TJ: 'Tajikistan',
            TK: 'Tokelau',
            TL: 'Timor-Leste',
            TM: 'Turkmenistan',
            TN: 'Tunisia',
            TO: 'Tonga',
            TR: 'Turkey',
            TT: 'Trinidad and Tobago',
            TV: 'Tuvalu',
            TW: 'Taiwan',
            TZ: 'Tanzania',
            UA: 'Ukraine',
            UG: 'Uganda',
            UM: 'United States Minor Outlying Islands',
            US: 'United States',
            UY: 'Uruguay',
            UZ: 'Uzbekistan',
            VA: 'Holy See (Vatican City)',
            VC: 'Saint Vincent and the Grenadines',
            VE: 'Venezuela',
            VG: 'British Virgin Islands',
            VI: 'Virgin Islands',
            VN: 'Vietnam',
            VU: 'Vanuatu',
            WF: 'Wallis and Futuna',
            WS: 'Samoa',
            XK: 'Kosovo',
            YE: 'Yemen',
            YT: 'Mayotte',
            ZA: 'South Africa',
            ZM: 'Zambia',
            ZW: 'Zimbabwe'
        };

        return land;
    }

    //statiske hjelpemetoder

}

export class Avtalestatus {

    constructor(avtale: { slettesDato: Date; proeveperiodeUtloepsdato: Date; avtalestatus: l.Avtalestatus; }) {
        const nå = moment(iDagKl00());
        const prøveperiodeUtløpsdato = moment(somKl0000(avtale.proeveperiodeUtloepsdato));

        this.erIProeveperiode = avtale.avtalestatus == l.Avtalestatus.prøveperiode;
        this.proeveperiodeUtloept = avtale.avtalestatus == l.Avtalestatus.prøveperiodeUtløpt;
        this.avtaleTerminert = avtale.avtalestatus == l.Avtalestatus.terminert;
        this.dagerIgjenAvProeveperiode = this.erIProeveperiode ? prøveperiodeUtløpsdato.diff(nå, 'days') : 0;

        this.skjedulertForSletting = this.proeveperiodeUtloept || this.avtaleTerminert;
        if (this.skjedulertForSletting) {
            const slettesDato = moment(avtale.slettesDato);
            this.dagerIgjenFoerSletting = slettesDato.isAfter(nå) ? slettesDato.diff(nå, 'days') : 0;
        }
    }

    erIProeveperiode: boolean;
    proeveperiodeUtloept: boolean;
    avtaleTerminert: boolean;
    dagerIgjenAvProeveperiode: number;
    skjedulertForSletting: boolean;
    dagerIgjenFoerSletting: number | null; //kun satt dersom skjedulertForSletting
}
